import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { InlineCta } from "@/components/site/InlineCta";
import { createFileRoute, Link } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../../components/site/Page";
import { GuideLinks } from "../../components/site/Guides";
import { KnowledgeHubNote } from "../../components/site/KnowledgeHubNote";
import { NewcomerNote } from "../../components/site/NewcomerNote";

const title = "NIE, empadronamiento and healthcare in Spain";
const description =
  "An overview of the NIE number, registration (empadronamiento) and healthcare for Swedish families moving to Spain.";

export const Route = createFileRoute("/en/nie-and-registration-in-spain")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: absoluteUrl("/en/nie-and-registration-in-spain") },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "en_GB" },
      ...ogImage(),
    ],
    links: seoLinks("/en/nie-and-registration-in-spain"),
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <PageHero
        eyebrow="Guide"
        title="NIE, registration and healthcare"
        intro="An overview of the administrative pieces that usually come up when a family settles in Spain. Rules and procedures change over time – always check current details with the relevant authority."
      />

      <NewcomerNote />

      <Prose>
        <h2>Read this as a map, not an answer key</h2>
        <p>
          We are a school, not a legal adviser. The purpose of this page is to orient you: which
          terms come up, roughly in what order, and how they connect to the start of school. The
          detailed walk-through – errand by errand, with documents, appointments and common pitfalls
          – is kept in one place in our knowledge hub, so we can keep it up to date instead of
          writing the same thing twice.
        </p>

        <h2>The order things happen in</h2>
        <ol>
          <li>
            <strong>NIE</strong> – the Spanish identity and tax number. It comes first, because
            housing, banking and insurance all depend on it. Children may need one too, depending on
            your situation. Most families have a lawyer or a gestoría handle the application, and
            when you buy a property your lawyer normally does it as part of the purchase.
          </li>
          <li>
            <strong>Housing contract</strong> – a rental agreement or deed, which is what gives you
            an address to register at.
          </li>
          <li>
            <strong>Empadronamiento (padrón)</strong> – registration in the municipal register of
            residents at the address where you actually live. It is asked for in a surprising number
            of contexts and must be updated if you move, for example from renting to owning.
          </li>
          <li>
            <strong>Certificado de registro / TIE</strong> – registration as an EU citizen, or a
            residence card for non-EU citizens.
          </li>
          <li>
            <strong>Bank, utilities and car</strong> – all of which need the NIE and usually the
            padrón.
          </li>
          <li>
            <strong>Healthcare</strong> – public cover, private insurance or a combination,
            depending on whether you work in Spain, are posted here, retired or self-sufficient.
          </li>
          <li>
            <strong>School</strong> – the application itself can be made at any point; you do not
            need the paperwork finished first.
          </li>
        </ol>
        <p>
          If you are moving permanently, the move is normally also reported to Skatteverket in
          Sweden, which affects civil registration, tax and certain benefits.
        </p>
      </Prose>

      <div className="mx-auto max-w-3xl px-5">
        <InlineCta locale="en" />
      </div>

      <Prose>
        <h2>What the school needs from you</h2>
        <p>
          Whichever healthcare route you choose, make sure it is sorted before school starts, and
          give us contact details plus any information about allergies, medication or particular
          needs. Our student health team works with both physical and psychosocial wellbeing and
          needs the right information from day one. The application also asks for your child's most
          recent school reports and vaccination records.
        </p>

        <h2>The full walk-through is in the knowledge hub</h2>
        <p>
          In <em>Paperwork and authorities: NIE, TIE, padrón and school</em> we go through the whole
          chain step by step: booking a cita previa when everything looks fully booked, what the
          padrón actually unlocks, the routes into healthcare including the EHIC, which documents to
          bring from Sweden and when an apostille or sworn translation is required.
        </p>
        <p>
          <Link
            className="text-primary link-quiet"
            to="/en/knowledge-hub/$slug"
            params={{ slug: "paperwork-and-authorities" }}
          >
            Open the material in the knowledge hub
          </Link>{" "}
          – or read our guide to{" "}
          <Link
            className="text-primary link-quiet"
            to="/en/moving-to-spain-with-children"
          >
            moving to Spain with children
          </Link>
          .
        </p>
      </Prose>


      <KnowledgeHubNote resource="myndigheter-och-papper" locale="en" />

      <GuideLinks current="/nie-och-folkbokforing-i-spanien" locale="en" />
      <CtaBand
        title="Ready to take the next step?"
        text="Applications are accepted on a rolling basis, and it's fine to start mid-term."
      />
      <div className="h-20" />
    </>
  );
}
